/**
 * WASD movement + mouse-look camera with 1st/3rd person toggle.
 * Press V to toggle camera mode.
 */
import * as THREE from 'three';

export type CameraMode = 'first' | 'third';

export interface PlayerControls {
  moveDir: THREE.Vector3;
  yaw: number;
  pitch: number;
  mode: CameraMode;
  running: boolean;
  /** Input state for per-frame polling */
  keys: Set<string>;
}

const THIRD_PERSON_DISTANCE = 5;
const THIRD_PERSON_HEIGHT = 2.5;
const FIRST_PERSON_HEIGHT = 1.7;
const MOUSE_SENSITIVITY = 0.002;
const WALK_SPEED = 4;
const RUN_SPEED = 8;
const PITCH_LIMIT = Math.PI / 2 - 0.05;

export function createControls(canvas: HTMLCanvasElement): PlayerControls {
  const state: PlayerControls = {
    moveDir: new THREE.Vector3(),
    yaw: 0,
    pitch: 0,
    mode: 'third',
    running: false,
    keys: new Set(),
  };

  // --- Keyboard ---
  window.addEventListener('keydown', (e) => {
    state.keys.add(e.code);
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') state.running = true;
    if (e.code === 'KeyV') {
      state.mode = state.mode === 'first' ? 'third' : 'first';
    }
  });
  window.addEventListener('keyup', (e) => {
    state.keys.delete(e.code);
    if (e.code === 'ShiftLeft' || e.code === 'ShiftRight') state.running = false;
  });

  // --- Mouse ---
  canvas.addEventListener('click', () => canvas.requestPointerLock());
  canvas.addEventListener('mousemove', (e) => {
    if (document.pointerLockElement !== canvas) return;
    state.yaw -= e.movementX * MOUSE_SENSITIVITY;
    state.pitch -= e.movementY * MOUSE_SENSITIVITY;
    state.pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, state.pitch));
  });

  return state;
}

/**
 * Compute movement direction from keyboard input (world-space, XZ only).
 */
export function updateMoveDir(controls: PlayerControls): void {
  const fwd = new THREE.Vector3(-Math.sin(controls.yaw), 0, -Math.cos(controls.yaw));
  const right = new THREE.Vector3(Math.cos(controls.yaw), 0, -Math.sin(controls.yaw));
  controls.moveDir.set(0, 0, 0);
  if (controls.keys.has('KeyW') || controls.keys.has('ArrowUp'))    controls.moveDir.add(fwd);
  if (controls.keys.has('KeyS') || controls.keys.has('ArrowDown'))  controls.moveDir.sub(fwd);
  if (controls.keys.has('KeyA') || controls.keys.has('ArrowLeft'))  controls.moveDir.sub(right);
  if (controls.keys.has('KeyD') || controls.keys.has('ArrowRight')) controls.moveDir.add(right);
  if (controls.moveDir.lengthSq() > 1) controls.moveDir.normalize();
  const speed = controls.running ? RUN_SPEED : WALK_SPEED;
  controls.moveDir.multiplyScalar(speed);
}

/**
 * Position the camera relative to a target (the character).
 */
export function updateCamera(
  camera: THREE.PerspectiveCamera,
  controls: PlayerControls,
  target: THREE.Object3D,
  delta: number,
): void {
  const dt = Math.min(delta, 0.1); // clamp spike frames
  const speed = controls.running ? RUN_SPEED : WALK_SPEED;
  target.position.x += controls.moveDir.x * dt;
  target.position.z += controls.moveDir.z * dt;

  const lookDir = new THREE.Vector3(
    -Math.sin(controls.yaw) * Math.cos(controls.pitch),
    Math.sin(controls.pitch),
    -Math.cos(controls.yaw) * Math.cos(controls.pitch),
  );

  if (controls.mode === 'first') {
    const eye = target.position.clone();
    eye.y += FIRST_PERSON_HEIGHT;
    camera.position.copy(eye);
    camera.lookAt(eye.clone().add(lookDir));
  } else {
    const eye = target.position.clone();
    eye.y += THIRD_PERSON_HEIGHT;
    eye.add(lookDir.clone().multiplyScalar(-THIRD_PERSON_DISTANCE));
    camera.position.lerp(eye, 8 * dt);
    camera.lookAt(target.position.clone().add(new THREE.Vector3(0, 1.5, 0)));
  }
}